import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { appearanceFor } from "../../shared/appearance";
import { ACCENTS, type Accent, type Settings } from "../../shared/types";
import { api } from "../api";
import { useAppPages, useModuleCommands, useModulePages, withPages } from "../modules";
import {
  dashboardHref,
  mailboxHome,
  mailHash,
  navigate,
  sourcePath,
  UNIFIED_INBOX,
} from "../router";
import {
  ACCENT_LABELS,
  applyTheme,
  installedThemes,
  reloadMe,
  SETTINGS_TABS,
} from "../settings-shared";
import { app, errorToast, openComposer, useFields } from "../store";
import { folderIcon, Icon, type IconName } from "./Icon";

type Command = {
  id: string;
  label: string;
  group: string;
  icon: IconName;
  hint?: string;
  run: () => void;
};

const ADMIN_TABS: [string, string][] = [
  ["users", "Users & addresses"],
  ["domains", "Domains"],
];

/** Scores how well `query` matches `text`: every word must appear; earlier and word-start hits rank higher. */
export const matchScore = (text: string, query: string): number => {
  const haystack = text.toLowerCase();
  let score = 0;
  for (const word of query.toLowerCase().split(/\s+/).filter(Boolean)) {
    const at = haystack.indexOf(word);
    if (at < 0) return -1;
    score += at === 0 ? 3 : /\W/.test(haystack[at - 1] ?? "") ? 2 : 1;
  }
  return score;
};

/** Ctrl+K / Cmd+K: jump anywhere, run any action, or search mail. */
export const CommandPalette = ({ onClose }: { onClose: () => void }) => {
  const { folders, labels, searches, me, mailboxes } = useFields(
    app,
    "folders",
    "labels",
    "searches",
    "me",
    "mailboxes",
  );
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);

  const settingsPages = useModulePages("settings");
  const appPages = useAppPages();
  const adminPages = useModulePages("admin");
  const moduleCommands = useModuleCommands();
  const commands = useMemo<Command[]>(() => {
    const go = (hash: string) => () => navigate(hash);
    const setTheme = (theme: Settings["theme"]) => async () => {
      applyTheme(theme);
      await api.patch("/account/settings", { theme }).catch(errorToast);
      await reloadMe();
    };
    const setAccent = (accent: Accent) => async () => {
      const appearance = {
        ...appearanceFor(me?.settings.theme ?? "system", me?.settings.appearance),
        customAccent: null,
      };
      applyTheme(me?.settings.theme ?? "system", accent, appearance);
      await api.patch("/account/settings", { accent, appearance }).catch(errorToast);
      await reloadMe();
    };
    return [
      {
        id: "compose",
        label: "Compose a message",
        group: "Actions",
        icon: "pencil",
        hint: "c",
        run: () => openComposer({ mode: "new", sourceId: null }),
      },
      ...folders.map((folder) => ({
        id: `folder:${folder.id}`,
        label: folder.name,
        group: "Folders",
        icon: folderIcon(folder.role),
        run: go(sourcePath({ kind: "folder", id: folder.id })),
      })),
      ...(
        [
          ["flagged", "Flagged", "star"],
          ["unread", "Unread", "mail"],
          ["vip", "VIP", "crown"],
          ["scheduled", "Scheduled", "clock"],
          ["snoozed", "Snoozed", "alarm"],
          ["all", "All mail", "mail"],
        ] as const
      ).map(([id, label, icon]) => ({
        id: `view:${id}`,
        label,
        group: "Folders",
        icon,
        run: go(sourcePath({ kind: "view", id })),
      })),
      ...labels.map((label) => ({
        id: `label:${label.id}`,
        label: label.name,
        group: "Labels",
        icon: "tag" as const,
        run: go(sourcePath({ kind: "label", id: label.id })),
      })),
      ...searches.map((search) => ({
        id: `search:${search.id}`,
        label: search.name,
        group: "Saved searches",
        icon: "search" as const,
        hint: search.query,
        run: go(sourcePath({ kind: "search", query: search.query })),
      })),
      ...(mailboxes.length > 1
        ? [
            ...((me?.settings.unifiedInbox ?? false)
              ? [
                  {
                    id: "box:all",
                    label: "All inboxes",
                    group: "Mailboxes",
                    icon: "inbox" as const,
                    hint: "g 0",
                    run: go(UNIFIED_INBOX),
                  },
                ]
              : []),
            ...mailboxes.map((box, index) => ({
              id: `box:${box.id}`,
              label: box.kind === "person" ? `Switch to your mailbox` : `Switch to ${box.address}`,
              group: "Mailboxes",
              icon: box.kind === "person" ? ("user" as const) : ("users" as const),
              ...(index < 9 ? { hint: `g ${index + 1}` } : {}),
              run: go(mailboxHome(box.kind === "person" ? null : box.id)),
            })),
          ]
        : []),
      { id: "contacts", label: "Contacts", group: "Go to", icon: "book", run: go("#/contacts") },
      ...appPages
        .filter((page) => !page.hidden)
        .map((page) => ({
          id: `page:${page.id}`,
          label: page.label,
          group: "Go to",
          icon: page.icon,
          run: go(page.href ?? `#/${page.id}`),
        })),
      ...moduleCommands.map((command) => ({
        id: `module:${command.id}`,
        label: command.label,
        group: "Actions",
        icon: command.icon ?? ("sparkles" as const),
        run: command.run,
      })),
      ...withPages<readonly [string, string]>(
        [...SETTINGS_TABS],
        ([id]) => id,
        settingsPages,
        (page) => [page.id, page.label],
      ).map(([id, label]) => ({
        id: `settings:${id}`,
        label: `Settings: ${label}`,
        group: "Settings",
        icon: "settings" as const,
        run: go(`#/settings/${id}`),
      })),
      ...(me?.isAdmin
        ? withPages<[string, string]>(
            ADMIN_TABS,
            ([id]) => id,
            adminPages,
            (page) => [page.id, page.label],
          ).map(([id, label]) => ({
            id: `admin:${id}`,
            label: `Admin: ${label}`,
            group: "Admin",
            icon: "shield" as const,
            run: () => location.assign(dashboardHref("admin", id)),
          }))
        : []),
      ...((me?.managedDomains ?? []).length > 0
        ? [
            {
              id: "domain-admin",
              label: "Domain admin",
              group: "Admin",
              icon: "globe" as const,
              run: () => location.assign("/domain-admin"),
            },
          ]
        : []),
      {
        id: "theme:system",
        label: "Match the system theme",
        group: "Appearance",
        icon: "sun",
        run: setTheme("system"),
      },
      ...installedThemes().map((theme) => ({
        id: `theme:${theme.id}`,
        label: `Use the ${theme.name} theme`,
        group: "Appearance",
        icon: "sun" as const,
        run: setTheme(theme.id),
      })),
      ...ACCENTS.map((accent) => ({
        id: `accent:${accent}`,
        label: `Accent color: ${ACCENT_LABELS[accent]}`,
        group: "Appearance",
        icon: "sun" as const,
        run: setAccent(accent),
      })),
    ];
  }, [
    folders,
    labels,
    searches,
    me,
    mailboxes,
    settingsPages,
    adminPages,
    appPages,
    moduleCommands,
  ]);

  const results = useMemo(() => {
    const trimmed = query.trim();
    const matched = trimmed
      ? commands
          .map((command) => ({
            command,
            score: matchScore(`${command.label} ${command.group}`, trimmed),
          }))
          .filter((entry) => entry.score >= 0)
          .sort((a, b) => b.score - a.score)
          .map((entry) => entry.command)
      : commands;
    const searchMail: Command[] = trimmed
      ? [
          {
            id: "search-mail",
            label: `Search mail for "${trimmed}"`,
            group: "Search",
            icon: "search",
            run: () => navigate(mailHash(`#/search/${encodeURIComponent(trimmed)}`)),
          },
        ]
      : [];
    return [...matched.slice(0, 50), ...searchMail];
  }, [commands, query]);

  useEffect(() => setActive(0), [query]);
  useEffect(() => {
    list.current?.querySelector(".palette-item.active")?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const choose = (command: Command | undefined) => {
    if (!command) return;
    onClose();
    command.run();
  };

  return (
    <div
      class="palette-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div class="palette" role="dialog" aria-modal="true" aria-label="Command palette">
        <div class="palette-input">
          <Icon name="search" size={17} />
          <input
            ref={input}
            placeholder="Go to a folder, run a command, or search mail…"
            aria-label="Command"
            aria-controls="palette-list"
            aria-activedescendant={results[active] ? `palette-${results[active]?.id}` : undefined}
            value={query}
            onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, results.length - 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
              } else if (event.key === "Enter") {
                event.preventDefault();
                choose(results[active]);
              } else if (event.key === "Escape") {
                event.preventDefault();
                onClose();
              }
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <div class="palette-list" id="palette-list" role="listbox" ref={list}>
          {results.length === 0 && <p class="muted palette-empty">Nothing matches.</p>}
          {results.map((command, index) => (
            <div
              key={command.id}
              id={`palette-${command.id}`}
              role="option"
              tabIndex={-1}
              aria-selected={index === active}
              class={`palette-item${index === active ? " active" : ""}`}
              onMouseMove={() => setActive(index)}
              onClick={() => choose(command)}
              onKeyDown={() => {}}
            >
              <Icon name={command.icon} size={16} />
              <span class="grow">{command.label}</span>
              {command.hint && <span class="palette-hint">{command.hint}</span>}
              <span class="palette-group">{command.group}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
